// Constants for the Euro pallet
export const PALLET_WIDTH = 800;    // mm
export const PALLET_LENGTH = 1200;  // mm
export const PALLET_HEIGHT = 144;   // mm
export const PALLET_WEIGHT = 25;    // kg

/**
 * Calculates possible pallet loading arrangements
 * @param {number} boxWidth 
 * @param {number} boxLength 
 * @param {number} boxHeight 
 * @param {number} boxWeight 
 * @param {number} maxHeight 
 * @param {boolean} enableMixedOrientation 
 * @param {boolean} includePalletWeight 
 * @param {number} palletWidth 
 * @param {number} palletLength 
 * @param {boolean} allowTipping 
 * @param {number} maxPalletWeight 
 * @param {number} toleranceMm 
 * @param {number} overhangWidth 
 * @param {number} overhangLength 
 * @returns {Array} Array of calculation results
 */
export function calculatePalletLoading(boxWidth, boxLength, boxHeight, boxWeight, maxHeight, enableMixedOrientation, includePalletWeight, palletWidth = PALLET_WIDTH, palletLength = PALLET_LENGTH, allowTipping = false, maxPalletWeight = Infinity, toleranceMm = 0, overhangWidth = 0, overhangLength = 0) {
    if (!Number.isFinite(boxWidth) || boxWidth <= 0 ||
        !Number.isFinite(boxLength) || boxLength <= 0 ||
        !Number.isFinite(boxHeight) || boxHeight <= 0 ||
        !Number.isFinite(boxWeight) || boxWeight < 0 ||
        !Number.isFinite(maxHeight) || maxHeight <= PALLET_HEIGHT ||
        !Number.isFinite(palletWidth) || palletWidth <= 0 ||
        !Number.isFinite(palletLength) || palletLength <= 0) {
        return [];
    }

    // Apply tolerances and overhangs for calculation
    const effBoxW = boxWidth + toleranceMm;
    const effBoxL = boxLength + toleranceMm;
    const effBoxH = boxHeight + toleranceMm;
    const effPalletW = palletWidth + overhangWidth;
    const effPalletL = palletLength + overhangLength;

    // Available height for boxes (minus pallet height)
    const availableHeight = maxHeight - PALLET_HEIGHT;

    // Calculate available volume on the pallet (theoretical)
    const availableVolume = effPalletW * effPalletL * availableHeight;

    // Calculate the physical possible arrangements
    let results = [
        // Arrangement 1: Upright, W-W, L-L
        {
            name: "Anordnung 1 (W-W, L-L)",
            boxesInWidth: Math.floor(effPalletW / effBoxW),
            boxesInLength: Math.floor(effPalletL / effBoxL),
            layers: Math.floor(availableHeight / effBoxH),
            boxWidth: boxWidth,
            boxLength: boxLength,
            boxHeight: boxHeight,
            effBoxW: effBoxW,
            effBoxL: effBoxL,
            effBoxH: effBoxH,
            isUprightBox: true,
            isLogisticallyRecommended: true,
            isPhysicallyAllowed: true,
            totalHeight: PALLET_HEIGHT + (Math.floor(availableHeight / effBoxH) * effBoxH)
        },
        // Arrangement 2: Upright, W-L, L-W
        {
            name: "Anordnung 2 (W-L, L-W)",
            boxesInWidth: Math.floor(effPalletW / effBoxL),
            boxesInLength: Math.floor(effPalletL / effBoxW),
            layers: Math.floor(availableHeight / effBoxH),
            boxWidth: boxLength,
            boxLength: boxWidth,
            boxHeight: boxHeight,
            effBoxW: effBoxL,
            effBoxL: effBoxW,
            effBoxH: effBoxH,
            isUprightBox: true,
            isLogisticallyRecommended: true,
            isPhysicallyAllowed: true,
            totalHeight: PALLET_HEIGHT + (Math.floor(availableHeight / effBoxH) * effBoxH)
        }
    ];

    if (allowTipping) {
        results.push(
            // Arrangement 3: Tipped, W-W
            {
                name: "Anordnung 3 (Gekippt, W-W)",
                boxesInWidth: Math.floor(effPalletW / effBoxW),
                boxesInLength: Math.floor(effPalletL / effBoxH),
                layers: Math.floor(availableHeight / effBoxL),
                boxWidth: boxWidth,
                boxLength: boxHeight,
                boxHeight: boxLength,
                effBoxW: effBoxW,
                effBoxL: effBoxH,
                effBoxH: effBoxL,
                isUprightBox: false,
                isLogisticallyRecommended: false,
                isPhysicallyAllowed: true,
                totalHeight: PALLET_HEIGHT + (Math.floor(availableHeight / effBoxL) * effBoxL)
            },
            // Arrangement 4: Tipped, W-H
            {
                name: "Anordnung 4 (Gekippt, W-H)",
                boxesInWidth: Math.floor(effPalletW / effBoxH),
                boxesInLength: Math.floor(effPalletL / effBoxW),
                layers: Math.floor(availableHeight / effBoxL),
                boxWidth: boxHeight,
                boxLength: boxWidth,
                boxHeight: boxLength,
                effBoxW: effBoxH,
                effBoxL: effBoxW,
                effBoxH: effBoxL,
                isUprightBox: false,
                isLogisticallyRecommended: false,
                isPhysicallyAllowed: true,
                totalHeight: PALLET_HEIGHT + (Math.floor(availableHeight / effBoxL) * effBoxL)
            },
            // Arrangement 5: Tipped side, W-L
            {
                name: "Anordnung 5 (Gekippt Seite, W-L)",
                boxesInWidth: Math.floor(effPalletW / effBoxL),
                boxesInLength: Math.floor(effPalletL / effBoxH),
                layers: Math.floor(availableHeight / effBoxW),
                boxWidth: boxLength,
                boxLength: boxHeight,
                boxHeight: boxWidth,
                effBoxW: effBoxL,
                effBoxL: effBoxH,
                effBoxH: effBoxW,
                isUprightBox: false,
                isLogisticallyRecommended: false,
                isPhysicallyAllowed: true,
                totalHeight: PALLET_HEIGHT + (Math.floor(availableHeight / effBoxW) * effBoxW)
            },
            // Arrangement 6: Tipped side, W-H
            {
                name: "Anordnung 6 (Gekippt Seite, W-H)",
                boxesInWidth: Math.floor(effPalletW / effBoxH),
                boxesInLength: Math.floor(effPalletL / effBoxLength),
                layers: Math.floor(availableHeight / effBoxW),
                boxWidth: boxHeight,
                boxLength: boxLength,
                boxHeight: boxWidth,
                effBoxW: effBoxH,
                effBoxL: effBoxL,
                effBoxH: effBoxW,
                isUprightBox: false,
                isLogisticallyRecommended: false,
                isPhysicallyAllowed: true,
                totalHeight: PALLET_HEIGHT + (Math.floor(availableHeight / effBoxW) * effBoxW)
            }
        );
    }

    // Deduplicate arrangements that are physically identical
    // Useful when box is a cube or square on some sides
    const uniqueResults = [];
    const seenConfigs = new Set();

    for (const res of results) {
        const configKey = `${res.effBoxW}x${res.effBoxL}x${res.effBoxH}`;
        if (!seenConfigs.has(configKey)) {
            seenConfigs.add(configKey);
            uniqueResults.push(res);
        }
    }
    results = uniqueResults;

    // If mixed orientation is enabled, optimize each arrangement
    if (enableMixedOrientation) {
        const optimizedResults = [];

        results.forEach(arrangement => {
            const optimized = fillRemainingStripsWithRotatedBoxes(arrangement, effPalletW, effPalletL);
            if (optimized.additionalBoxesPerLayer > 0) {
                optimizedResults.push(optimized);
            } else {
                optimizedResults.push(arrangement);
            }
        });

        results = optimizedResults;
    }

    // Finalize properties, weights, volumes, and flags
    results.forEach(result => {
        if (!result.layerLayout) {
            // Generate basic layer layout for standard arrangements
            result.layerLayout = [];
            for (let i = 0; i < result.boxesInWidth; i++) {
                for (let j = 0; j < result.boxesInLength; j++) {
                    result.layerLayout.push({
                        x: i * result.effBoxW,
                        y: j * result.effBoxL,
                        width: result.effBoxW,
                        length: result.effBoxL,
                        isAlternateOrientation: false
                    });
                }
            }
            result.boxesPerLayer = result.boxesInWidth * result.boxesInLength;
            result.totalBoxes = result.boxesPerLayer * result.layers;
        }

        result.totalBoxWeight = result.totalBoxes * boxWeight;
        result.totalWeight = includePalletWeight ?
            result.totalBoxWeight + PALLET_WEIGHT :
            result.totalBoxWeight;

        result.exceedsWeightLimit = maxPalletWeight > 0 && result.totalWeight > maxPalletWeight;

        // Calculate theoretical volume utilization
        // Use nominal sizes for the volume quote, not the tolerance-bloated size
        const nominalBoxVolume = boxWidth * boxLength * boxHeight;
        const totalNominalBoxVolume = result.totalBoxes * nominalBoxVolume;
        const nominalAvailableVolume = palletWidth * palletLength * availableHeight;

        result.theoreticalVolumeUtilization = (totalNominalBoxVolume / nominalAvailableVolume) * 100;

        // Ensure values missing don't break UI
        if (isNaN(result.theoreticalVolumeUtilization)) result.theoreticalVolumeUtilization = 0;
    });

    // Sort: 1. Maximize total boxes, 2. Maximize volume utilization, 3. Prefer recommended
    results.sort((a, b) => {
        // First sort by whether the weight limit is exceeded
        if (a.exceedsWeightLimit !== b.exceedsWeightLimit) {
            return a.exceedsWeightLimit ? 1 : -1;
        }

        // Then sort by total boxes
        if (b.totalBoxes !== a.totalBoxes) {
            return b.totalBoxes - a.totalBoxes;
        }

        // Then sort by theoretical volume
        if (Math.abs(b.theoreticalVolumeUtilization - a.theoreticalVolumeUtilization) > 0.1) {
            return b.theoreticalVolumeUtilization - a.theoreticalVolumeUtilization;
        }

        // Tie breaker: prioritize recommended (upright)
        return (a.isLogisticallyRecommended === b.isLogisticallyRecommended) ? 0 : a.isLogisticallyRecommended ? -1 : 1;
    });

    return results;
}

/**
 * Heuristic function to fill remaining rest strips with rotated boxes.
 * Note: This is not a perfect 2D bin packing solver, it's a heuristic for rest strips.
 */
function fillRemainingStripsWithRotatedBoxes(arrangement, palletWidth, palletLength) {
    const optimizedArrangement = { ...arrangement };
    optimizedArrangement.layerLayout = [];

    // Add standard boxes first (all with the same orientation)
    for (let i = 0; i < arrangement.boxesInWidth; i++) {
        for (let j = 0; j < arrangement.boxesInLength; j++) {
            optimizedArrangement.layerLayout.push({
                x: i * arrangement.effBoxW,
                y: j * arrangement.effBoxL,
                width: arrangement.effBoxW,
                length: arrangement.effBoxL,
                isAlternateOrientation: false
            });
        }
    }

    // Calculate remaining space on the pallet
    const usedWidth = arrangement.boxesInWidth * arrangement.effBoxW;
    const usedLength = arrangement.boxesInLength * arrangement.effBoxL;

    const remainingWidth = palletWidth - usedWidth;
    const remainingLength = palletLength - usedLength;

    let additionalBoxes = 0;

    // Try to fill the remaining width with boxes rotated 90 degrees
    if (remainingWidth >= arrangement.effBoxL) {
        const boxesInRemainingWidth = Math.floor(remainingWidth / arrangement.effBoxL);
        const boxesInLength = Math.floor(palletLength / arrangement.effBoxW);

        additionalBoxes += boxesInRemainingWidth * boxesInLength;

        for (let i = 0; i < boxesInRemainingWidth; i++) {
            for (let j = 0; j < boxesInLength; j++) {
                optimizedArrangement.layerLayout.push({
                    x: usedWidth + i * arrangement.effBoxL,
                    y: j * arrangement.effBoxW,
                    width: arrangement.effBoxL,
                    length: arrangement.effBoxW,
                    isAlternateOrientation: true
                });
            }
        }
    }

    // Try to fill the remaining length with boxes rotated 90 degrees
    if (remainingLength >= arrangement.effBoxW) {
        const boxesInRemainingLength = Math.floor(remainingLength / arrangement.effBoxW);
        const boxesInWidth = Math.floor(usedWidth / arrangement.effBoxL);

        additionalBoxes += boxesInRemainingLength * boxesInWidth;

        for (let i = 0; i < boxesInWidth; i++) {
            for (let j = 0; j < boxesInRemainingLength; j++) {
                optimizedArrangement.layerLayout.push({
                    x: i * arrangement.effBoxL,
                    y: usedLength + j * arrangement.effBoxW,
                    width: arrangement.effBoxL,
                    length: arrangement.effBoxW,
                    isAlternateOrientation: true
                });
            }
        }
    }

    optimizedArrangement.additionalBoxesPerLayer = additionalBoxes;
    optimizedArrangement.boxesPerLayer = (arrangement.boxesInWidth * arrangement.boxesInLength) + additionalBoxes;
    optimizedArrangement.totalBoxes = optimizedArrangement.boxesPerLayer * optimizedArrangement.layers;
    optimizedArrangement.isMixedOrientation = true;

    return optimizedArrangement;
}
